/*
 * SPDX-FileCopyrightText: 2022-2024 CERN.
 * SPDX-License-Identifier: MIT
 */

import React, { Component } from "react";
import PropTypes from "prop-types";
import { i18next } from "@translations/invenio_app_rdm/i18next";
import { withCancel, http } from "react-invenio-forms";
import {
  Placeholder,
  Divider,
  Container,
  Header,
  Item,
  Button,
  Message,
  Grid,
} from "semantic-ui-react";
import Overridable from "react-overridable";
import RecordsResultsListItem from "@js/invenio_app_rdm/components/RecordsResultsListItem";
import isEmpty from "lodash/isEmpty";
import { buildUID } from "react-searchkit";

export class RecordsList extends Component {
  constructor(props) {
    super(props);

    this.state = {
      data: { hits: [] },
      isLoading: false,
      error: null,
    };
  }

  componentDidMount() {
    this.fetchData();
  }

  async componentDidUpdate() {
    await window.MathJax?.typesetPromise();
  }

  componentWillUnmount() {
    this.cancellableFetch && this.cancellableFetch.cancel();
  }

  fetchData = async () => {
    const { fetchUrl } = this.props;
    this.setState({ isLoading: true });

    this.cancellableFetch = withCancel(
      http.get(fetchUrl, {
        headers: {
          Accept: "application/vnd.inveniordm.v1+json",
        },
      })
    );

    try {
      const response = await this.cancellableFetch.promise;
      this.setState({ data: response.data.hits, isLoading: false });
    } catch (error) {
      console.error(error);
      this.setState({ error: error.response.data.message, isLoading: false });
    }
  };

  renderPlaceHolder = () => {
    const { title } = this.props;

    return (
      <Container>
        <Header as="h2">{title}</Header>
        {Array.from(Array(10)).map((item, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={index}>
            <Placeholder fluid className="rel-mt-3">
              <Placeholder.Header>
                <Placeholder.Line />
              </Placeholder.Header>

              <Placeholder.Paragraph>
                <Placeholder.Line />
              </Placeholder.Paragraph>

              <Placeholder.Paragraph>
                <Placeholder.Line />
                <Placeholder.Line />
                <Placeholder.Line />
              </Placeholder.Paragraph>
            </Placeholder>

            {index < 9 && <Divider className="rel-mt-2 rel-mb-2" />}
          </div>
        ))}
      </Container>
    );
  };

  render() {
    const { isLoading, data, error } = this.state;
    const { title, appName } = this.props;

    const listItems = data.hits?.map((record) => {
      return (
        <RecordsResultsListItem result={record} key={record.id} appName={appName} />
      );
    });

    const midpoint = Math.ceil(listItems.length / 2);
    const leftItems = listItems.slice(0, midpoint);
    const rightItems = listItems.slice(midpoint);
    const rows = [];

    for (let i = 0; i < Math.max(leftItems.length, rightItems.length); i++) {
      rows.push({
        left: leftItems[i],
        right: rightItems[i],
      });
    }

    return (
      <>
        {isLoading && this.renderPlaceHolder()}

        {!isLoading && !error && !isEmpty(listItems) && (
          <Container>
            <Header as="h2">{title}</Header>

            {/* <Item.Group relaxed link divided>
              {listItems}
            </Item.Group> */}
            <Grid columns={2} stackable className="frontpage-records">
              {rows.map((row, idx) => (
                <Grid.Row key={idx}>
                  <Grid.Column>
                    {row.left && (
                      <Item.Group relaxed link divided>
                        {row.left}
                      </Item.Group>
                    )}
                  </Grid.Column>
                  <Grid.Column>
                    {row.right && (
                      <Item.Group relaxed link divided>
                        {row.right}
                      </Item.Group>
                    )}
                  </Grid.Column>
                </Grid.Row>
              ))}
            </Grid>

            <Container textAlign="center">
              <Button href="/search" aria-label={i18next.t("Load more search results")}>
                {i18next.t("More")}
              </Button>
            </Container>

            {error && <Message content={error} error icon="warning sign" />}
          </Container>
        )}
      </>
    );
  }
}

RecordsList.propTypes = {
  title: PropTypes.string.isRequired,
  fetchUrl: PropTypes.string.isRequired,
  appName: PropTypes.string,
};

RecordsList.defaultProps = {
  appName: "",
};

export class RecordsListOverridable extends Component {
  render() {
    const { title, fetchUrl, appName } = this.props;
    return (
      <Overridable
        id={buildUID("layout", "", appName)}
        title={title}
        fetchUrl={fetchUrl}
        appName={appName}
      >
        <RecordsList title={title} fetchUrl={fetchUrl} appName={appName} />
      </Overridable>
    );
  }
}

RecordsListOverridable.propTypes = {
  title: PropTypes.string.isRequired,
  fetchUrl: PropTypes.string.isRequired,
  appName: PropTypes.string,
};

RecordsListOverridable.defaultProps = {
  appName: "",
};
